import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const TemplateDialog = ({ edit, setEdit, onSaved }) => {
  const items = edit?.items || [];
  const setItem = (n, k, v) => setEdit({ ...edit, items: items.map((it, i) => (i === n ? { ...it, [k]: v } : it)) });
  const save = async () => {
    const { id, name, description } = edit;
    try { id ? await api.patch(`/prevencion/templates/${id}`, { name, description, items }) : await api.post("/prevencion/templates", { name, description, items }); toast.success("Checklist guardado"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? `Editar checklist (nueva versión v${edit.version + 1})` : "Nuevo checklist"}</DialogTitle></DialogHeader>
        {edit && <div className="space-y-3">
          <Field label="Nombre *"><Input data-testid="template-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          <Field label="Descripción"><Input data-testid="template-desc-input" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
          {items.map((it, n) => (
            <div key={n} className="flex items-center gap-2">
              <Input data-testid={`template-item-${n}`} value={it.text} placeholder={`Ítem ${n + 1}`} onChange={(e) => setItem(n, "text", e.target.value)} />
              <label className="flex items-center gap-1 text-xs whitespace-nowrap"><Checkbox data-testid={`template-critical-${n}`} checked={it.critical} onCheckedChange={(v) => setItem(n, "critical", !!v)} />Crítico</label>
              <Button data-testid={`template-remove-${n}`} size="icon" variant="ghost" onClick={() => setEdit({ ...edit, items: items.filter((_, i) => i !== n) })}><Trash2 size={15} /></Button>
            </div>))}
          <Button data-testid="template-add-item-btn" variant="outline" size="sm" className="rounded-full" onClick={() => setEdit({ ...edit, items: [...items, { text: "", critical: false }] })}><Plus size={14} className="mr-1" />Ítem</Button>
        </div>}
        <DialogFooter><Button data-testid="template-save-btn" className="rounded-full" onClick={save}>Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Templates() {
  const { can } = useAuth();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/prevencion/templates", { params: { all: true } }).then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => { try { await api.post(`/prevencion/templates/${t.id}/toggle`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const gest = can("prevencion.checklists.gestionar");
  return (
    <div data-testid="prev-templates-page">
      <PageHeader eyebrow="Prevención" title="Checklists" subtitle="Plantillas versionadas. Cada edición crea una nueva versión; las inspecciones guardan la versión usada."
        actions={gest && <Button data-testid="template-new-btn" className="rounded-full" onClick={() => setEdit({ name: "", description: "", items: [{ text: "", critical: false }] })}><Plus size={15} className="mr-1" />Checklist</Button>} />
      <div className="syc-card overflow-hidden">{!list ? <div className="p-5">Cargando…</div> : list.length === 0 ? <EmptyState title="Sin checklists" /> : list.map((t) => (
        <div key={t.id} data-testid={`template-row-${t.id}`} className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] text-sm">
          <div className="flex-1 min-w-0"><b>{t.name}</b> <span className="text-xs text-[#5b6656]">v{t.version} · {t.items.length} ítems · {t.items.filter((i) => i.critical).length} críticos</span></div>
          <StatusBadge status={t.status === "activo" ? "activo" : "inactivo"} label={t.status} />
          {gest && <><Button data-testid={`template-edit-${t.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => setEdit({ ...t })}>Editar</Button>
            <Button data-testid={`template-toggle-${t.id}`} size="sm" variant="ghost" onClick={() => toggle(t)}>{t.status === "activo" ? "Desactivar" : "Activar"}</Button></>}
        </div>))}</div>
      <TemplateDialog edit={edit} setEdit={setEdit} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
